import { useQuery } from '@apollo/react-hooks';
import { useMemo } from 'react';
import { query as GET_ACCOUNTS } from 'pages/app/Accounts/gql/queries/get-accounts';

// Every active account for the W-9 account picker (w9-plan §6.2). The
// section is platform-wide, so this is the same list the admin account
// switcher reads (hooks/useAccountVenues), kept here with the track /
// series split the design groups the picker by: an account that only
// runs series is a series; everything else is a track.

export const accountKind = (account) => {
  const series = (account && account.series_ids) || [];
  const tracks = (account && account.track_ids) || [];
  return series.length && !tracks.length ? 'series' : 'track';
};

export const toW9Account = (account) => ({
  id: account.id,
  name: account.business_dba || account.legal_name || `Account ${account.id}`,
  kind: accountKind(account),
});

const byName = (a, b) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

// Active accounts, A–Z.
export const w9AccountsOf = (results) =>
  (results || [])
    .filter((a) => a && !a.inactive)
    .map(toW9Account)
    .sort(byName);

// FormsSelect groups: Tracks, then Series (either left out when empty).
export const groupAccounts = (accounts) => {
  const option = (a) => ({ label: a.name, value: String(a.id) });
  const tracks = accounts.filter((a) => a.kind === 'track').map(option);
  const series = accounts.filter((a) => a.kind === 'series').map(option);
  return [
    ...(tracks.length ? [{ label: 'Tracks', options: tracks }] : []),
    ...(series.length ? [{ label: 'Series', options: series }] : []),
  ];
};

const useW9Accounts = () => {
  const { data, loading, error } = useQuery(GET_ACCOUNTS, {
    variables: { input: { limit: 1000 } },
  });
  const results = data && data.getAccounts && data.getAccounts.results;
  const accounts = useMemo(() => w9AccountsOf(results), [results]);
  return { accounts, loading: !!loading, error: error || null };
};

export default useW9Accounts;
